import { gregorianToEthiopic } from "ethiopian-calendar-finova";
import { LoginAccount, OrgUnit, TreeSelectOption } from "types";

export function getEthiopicDateTime({
  datetime = new Date(),
  displayHour = false,
  timeFormat = "12h",
}) {
  const dateObj = new Date(datetime);
  const ethiopic = gregorianToEthiopic(dateObj);
  // const ethiopic = gregorianToEthiopic(dateObj, timeFormat);
  const hourStr =
    displayHour && ethiopic.hour !== undefined
      ? ethiopic.hour.toString().padStart(2, "0")
      : "";
  const minuteStr =
    displayHour && ethiopic.minute !== undefined
      ? ethiopic.minute.toString().padStart(2, "0")
      : "";

  const timeSuffix =
    displayHour && timeFormat === "12h" && ethiopic.ampm
      ? ` ${ethiopic.ampm}`
      : "";

  return (
    <>
      {ethiopic.day}/{ethiopic.month}/{ethiopic.year} EC
      {displayHour ? ` ${hourStr}:${minuteStr}${timeSuffix}` : ""}
    </>
  );
}

export const getInitials = (name: string) => {
  if (!name) return "";
  const parts = name.split(" ");
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

export const DateView = (
  date: Date | string | null | undefined,
  showTime = false,
) =>
  date
    ? // ? new Date(date).toLocaleString("en-US", {
      //     year: "numeric",
      //     month: "long",
      //     day: "numeric",
      //     ...(showTime && {
      //       hour: "2-digit",
      //       minute: "2-digit",
      //       hour12: true,
      //     }),
      // })
      getEthiopicDateTime({
        datetime: new Date(date),
        displayHour: showTime,
      })
    : "";

export const errorHandler = (error: {
  response?: {
    data?: any;
    status?: number;
    statusText?: string;
    headers?: any;
  };
  request?: any;
  message?: string;
}): string => {
  // Helper: Check if a string looks like HTML
  const isHtmlString = (str: string): boolean => {
    return /<[a-z][\s\S]*>/i.test(str.trim().substring(0, 200));
  };

  if (error.response) {
    const { status, statusText, data, headers } = error.response;
    const contentType = headers?.["content-type"] || "";

    // 1. If it's a JSON response with a detail field
    if (data && typeof data === "object" && data.detail) {
      return data.detail;
    }

    // 2. Handle specific status codes
    if (status === 500) {
      return "Internal server error. Please try again later or contact support.";
    }
    if (status === 404) {
      return "The requested resource was not found.";
    }
    if (status === 403) {
      return "You do not have permission to perform this action.";
    }
    if (status === 401) {
      return "Unauthorized. Please log in again.";
    }

    // 3. If response is HTML (common for 500 errors, debug off)
    if (
      contentType.includes("text/html") ||
      (typeof data === "string" && isHtmlString(data))
    ) {
      return `Server error (${status}). Please contact support if the issue persists.`;
    }

    // 4. If data is a string but not HTML, return first 100 chars
    if (typeof data === "string") {
      return data.substring(0, 200);
    }

    // 5. Fallback to status text
    return `Error ${status}: ${statusText || "Something went wrong"}`;
  } else if (error.request) {
    return "Network Error: No response received from the server. Please check your connection.";
  } else {
    return `Unexpected Error: ${error.message || "Unknown error"}`;
  }
};

export const errorHandlerOnForm = (error: {
  response?: {
    data?: Record<string, string[] | string> | string | any[];
    status?: number;
    statusText?: string;
  };
  request?: any;
  message?: string;
}): string => {
  // Helper: Convert any data to a plain string (handles arrays of characters)
  const toPlainString = (value: any): string => {
    if (typeof value === "string") return value;
    if (Array.isArray(value)) {
      // If it's an array of characters (like the 0: 'h', 1: 't'... output)
      if (
        value.every((item) => typeof item === "string" && item.length === 1)
      ) {
        return value.join("");
      }
      return value.join(", ");
    }
    if (value && typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return "Unknown error format";
      }
    }
    return String(value);
  };

  // Helper: Check if a string looks like HTML
  const looksLikeHtml = (str: string): boolean => {
    const trimmed = str.trim();
    return (
      trimmed.startsWith("<") &&
      /<[a-z][\s\S]*>/i.test(trimmed.substring(0, 200))
    );
  };

  if (error.response && error.response.data) {
    const rawData = error.response.data;
    const status = error.response.status;
    const plainData = toPlainString(rawData);

    // 1. If the response is HTML (typical for 500 errors with DEBUG=False)
    if (looksLikeHtml(plainData)) {
      if (status === 500) {
        return "Internal server error. Please try again later or contact support.";
      }
      return `Server error (${status}). Please try again.`;
    }

    // 2. Handle structured error objects
    if (typeof rawData === "object" && !Array.isArray(rawData)) {
      // DRF non_field_errors
      if (rawData.non_field_errors && Array.isArray(rawData.non_field_errors)) {
        return rawData.non_field_errors.join("\n");
      }
      if (typeof rawData.detail === "string") return rawData.detail;
      if (typeof rawData.message === "string") return rawData.message;

      // Field‑by‑field errors
      const messages = Object.entries(rawData)
        .map(([field, errors]) => {
          const errorText = toPlainString(errors);
          return `${field}: ${errorText}`;
        })
        .filter(Boolean)
        .join("\n");

      if (messages) return messages;
    }

    // 3. If we got a plain string (but not HTML)
    if (plainData) {
      // Limit length to avoid giant messages
      return plainData.length > 500
        ? plainData.substring(0, 500) + "…"
        : plainData;
    }

    return "An error occurred while submitting the form.";
  }

  if (error.request) {
    return "Network Error: No response received from the server. Please check your connection.";
  }

  return `Unexpected Error: ${error.message || "Unknown error"}`;
};

export const hasRole = (
  loginRoles: string[] | undefined,
  roleToCheck: string[] | undefined,
  loginData?: LoginAccount | null,
): boolean => {
  if (!roleToCheck || roleToCheck.length === 0) {
    return false;
  }

  if (loginData) {
    return roleToCheck.some((role) =>
      role === "Police"
        ? loginData?.role === "Member" || loginData?.is_police
        : role === "PoliceCoordinator"
          ? loginData?.user?.role === "police_coordinator"
          : false,
    );
  } else if (!loginRoles || loginRoles.length === 0) {
    return false;
  }
  return roleToCheck.some((role) => loginRoles.includes(role));
};

export function splitCamelCase(str: string): string {
  return str.replace(/([a-z])([A-Z])/g, "$1 $2");
}

export function getFileType(filePath: string): string {
  const ext = filePath.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "pdf":
      return "PDF";
    case "doc":
    case "docx":
      return "Word";
    case "xls":
    case "xlsx":
      return "Excel";
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
      return "Image";
    default:
      return ext ? ext.toUpperCase() + " File" : "Unknown";
  }
}

export const constructFileUrl = (doc: any, partialUrl: string): string => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  // If baseUrl is provided, use it with partialUrl and document_uuid
  if (baseUrl) {
    // Remove trailing slash if exists
    const cleanBaseUrl = baseUrl.replace(/\/$/, "");
    const constructedUrl = `${cleanBaseUrl}${partialUrl}${doc.document_uuid}`;
    return constructedUrl;
  }

  // If no baseUrl but document_path looks like a full URL, use it directly
  if (doc.document_path.startsWith("http")) {
    return doc.document_path;
  }

  return "";
};

export const getAvatarBgColor = (name: string) => {
  const colors = ["bg-[#AA232F]"];
  if (!name) return colors[0];
  const hash = name
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

export const mapToTreeSelectOptions = (node: OrgUnit): TreeSelectOption => {
  return {
    label: node.unit_name,
    value: node.uu_id,
    children: node.children?.map(mapToTreeSelectOptions),
  };
};

export function formatDate(dateString: string): string {
  const date = new Date(dateString);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const day = date.getDate();
  const year = date.getFullYear();
  const month = monthNames[date.getMonth()];

  // Helper to get ordinal suffix
  const getOrdinal = (n: any) => {
    if (n > 3 && n < 21) return `${n}th`;
    switch (n % 10) {
      case 1:
        return `${n}st`;
      case 2:
        return `${n}nd`;
      case 3:
        return `${n}rd`;
      default:
        return `${n}th`;
    }
  };

  return `${month} ${getOrdinal(day)} ${year}`;
}

export const formatPhoneNumber = (value: string) => {
  // Remove all non-digits
  const phone = value.replace(/\D/g, "");

  // US formatting
  if (phone.length <= 3) return phone;
  if (phone.length <= 6) return `(${phone.slice(0, 3)}) ${phone.slice(3)}`;
  return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
};

export const isPhoneCorrect = (phone: string) => {
  if (phone.length < 10) return false;
  return true;
};

export const isEmailCorrect = (email: string) => {
  if (!/\S+@\S+\.\S+/.test(email)) return false;
  return true;
};
